import { DOCUMENT } from '@angular/common';
import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';

/** Set on `<html>` while the user is moving around with the keyboard. */
export const KEYBOARD_NAV_CLASS = 'pg-keyboard-nav';

/** The keys that move focus or a selection — not the ones that act or type. */
const NAVIGATION_KEYS = new Set([
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
]);

/**
 * Tells the stylesheet whether the focus ring is wanted.
 *
 * POC-9587 (and before it POC-9483, POC-9497, the user-menu rings of
 * 2026-09-10): the ring kept appearing for focus nobody navigated to. The
 * browser's `:focus-visible` is a guess — right after a page load, or after
 * any key at all, it calls programmatic focus visible — and the CDK's dialog
 * restores focus to its trigger as "keyboard" focus when Esc closed it. Each
 * case got its own exclusion, and QA kept finding the next one.
 *
 * The rule is a single one now: the ring is drawn only while the user is
 * navigating with the keyboard. A navigation key (Tab, the arrows, Home/End,
 * PageUp/Down) turns the mode on; the first pointer press turns it off. Esc,
 * Enter, typing and programmatic focus do not start it, so a dialog closed
 * with Esc by a mouse user hands focus back silently, while a keyboard user
 * who tabbed in still sees where focus went. The stylesheet reads the class
 * (`styles.scss`, rule A2).
 */
export function provideKeyboardNavigationMode(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => trackKeyboardNavigation(inject(DOCUMENT))),
  ]);
}

/**
 * The initializer's body, callable on its own so the switching can be tested
 * without an application.
 */
export function trackKeyboardNavigation(document: Document): void {
  const root = document.documentElement;

  document.addEventListener(
    'keydown',
    (event) => {
      if (NAVIGATION_KEYS.has(event.key))
        root.classList.add(KEYBOARD_NAV_CLASS);
    },
    { capture: true, passive: true }
  );

  // Mouse, touch and pen alike; `pointerdown` comes before focus moves, so the
  // click's own focus is already a pointer one when the ring rule is checked.
  document.addEventListener(
    'pointerdown',
    () => root.classList.remove(KEYBOARD_NAV_CLASS),
    { capture: true, passive: true }
  );
}
